import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Typography,
  InputAdornment,
} from '@mui/material';
import {
  CallSplit as BranchIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { getScopeSwimlane, createScope, deleteScope, assignIssuesToScope } from '../../services/releaseManagementApi';
import ScopeSwimlanes from '../../components/release/ScopeSwimlanes';

/* ================================================================ */
/*  Map API response → ScopeSwimlanes props                          */
/* ================================================================ */

const STATUS_MAP = {
  'planning':    'Planning',
  'in_progress': 'Active',
  'completed':   'Merged',
  'merged':      'Merged',
  'blocked':     'Blocked',
};

const SEVERITY_MAP = {
  'critical': 'Critical',
  'major':    'Major',
  'minor':    'Minor',
  'trivial':  'Trivial',
};

const ISSUE_STATUS_MAP = {
  'open':                 'Open',
  'in development':       'InProgress',
  'testing in progress':  'InProgress',
  'resolved':             'Fixed',
  'fix deployed':         'Verified',
  'fix not working':      'Open',
  'closed':               'Closed',
};

function mapIssue(raw) {
  const rawStatus = (raw.status || '').toLowerCase();
  const rawSeverity = (raw.severity || '').toLowerCase();
  return {
    id: raw.jira_bug_id || `QA-${raw.id}`,
    _routeId: raw.id,
    title: raw.title,
    module: raw.module_name || '',
    sub_module: raw.sub_module_name || '',
    feature: raw.feature_name || '',
    severity: SEVERITY_MAP[rawSeverity] || 'Major',
    status: ISSUE_STATUS_MAP[rawStatus] || 'Open',
    assignee: raw.assignee_name || '',
  };
}

function mapScope(raw) {
  const rawStatus = (raw.status || '').toLowerCase();
  const sp = raw.story_progress || {};
  return {
    id: raw.id ?? 'unscoped',
    name: raw.name,
    branch_name: raw.branch_name || '',
    status: STATUS_MAP[rawStatus] || 'Planning',
    issues: (raw.issues || []).map(mapIssue),
    story_progress: {
      total: sp.total_stories || 0,
      done: sp.done_stories || 0,
      remaining: sp.remaining_stories || 0,
    },
  };
}

/* ================================================================ */
/*  ScopeSwimlaneView (data wrapper)                                 */
/* ================================================================ */

const ScopeSwimlaneView = ({ releaseId, onNavigateToIssues, onNavigateToStories }) => {
  const [swimlaneData, setSwimlaneData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newScope, setNewScope] = useState({
    name: '',
    branch_name: '',
    description: '',
    scope_type: 'feature',
  });
  const [creating, setCreating] = useState(false);

  // Assign-issues dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTargetScope, setAssignTargetScope] = useState(null);
  const [selectedIssueIds, setSelectedIssueIds] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');

  const fetchSwimlane = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getScopeSwimlane(releaseId);
      setSwimlaneData(response.data);
    } catch (err) {
      console.error('Error fetching swimlane:', err);
      setError(err.response?.data?.detail || 'Failed to load scope swimlane');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSwimlane();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseId]);

  const handleCreateScope = async () => {
    if (!newScope.name.trim()) return;
    try {
      setCreating(true);
      await createScope(releaseId, newScope);
      setCreateDialogOpen(false);
      setNewScope({ name: '', branch_name: '', description: '', scope_type: 'feature' });
      fetchSwimlane();
    } catch (err) {
      console.error('Error creating scope:', err);
      setError(err.response?.data?.detail || 'Failed to create scope');
    } finally {
      setCreating(false);
    }
  };

  /* ── Assign issues helpers ─────────────────── */

  // Collect all unscoped issues from the swimlane data (issues not yet assigned to any scope)
  const unscopedIssues = useMemo(() => {
    if (!swimlaneData?.scopes) return [];
    const unscopedLane = swimlaneData.scopes.find(s => s.id === null || s.id === undefined);
    return (unscopedLane?.issues || []);
  }, [swimlaneData]);

  const filteredUnscopedIssues = useMemo(() => {
    if (!assignSearch.trim()) return unscopedIssues;
    const q = assignSearch.toLowerCase();
    return unscopedIssues.filter(
      i => (i.title || '').toLowerCase().includes(q)
        || String(i.id).includes(q)
        || (i.jira_bug_id || '').toLowerCase().includes(q)
    );
  }, [unscopedIssues, assignSearch]);

  const handleOpenAssignDialog = (scope) => {
    setAssignTargetScope(scope);
    setSelectedIssueIds([]);
    setAssignSearch('');
    setAssignDialogOpen(true);
  };

  const handleToggleIssue = (issueId) => {
    setSelectedIssueIds(prev =>
      prev.includes(issueId) ? prev.filter(id => id !== issueId) : [...prev, issueId]
    );
  };

  const handleAssignIssues = async () => {
    if (selectedIssueIds.length === 0 || !assignTargetScope) return;
    try {
      setAssigning(true);
      await assignIssuesToScope(releaseId, assignTargetScope.id, selectedIssueIds);
      setAssignDialogOpen(false);
      setAssignTargetScope(null);
      setSelectedIssueIds([]);
      fetchSwimlane();
    } catch (err) {
      console.error('Error assigning issues:', err);
      setError(err.response?.data?.detail || 'Failed to assign issues');
    } finally {
      setAssigning(false);
    }
  };

  /* ── Loading / Error states ──────────────────── */

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  /* ── Map data into the presentational shape ──── */
  const scopes = (swimlaneData?.scopes || []).map(mapScope);

  return (
    <Box>
      <ScopeSwimlanes
        scopes={scopes}
        onAddScope={() => setCreateDialogOpen(true)}
        onAssignIssues={handleOpenAssignDialog}
        onNavigateToIssues={onNavigateToIssues}
        onNavigateToStories={onNavigateToStories}
      />

      {/* Create Scope Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Scope</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Scope Name"
            fullWidth
            required
            value={newScope.name}
            onChange={(e) => setNewScope({ ...newScope, name: e.target.value })}
            placeholder="e.g., Payment Gateway Revamp"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Branch Name"
            fullWidth
            value={newScope.branch_name}
            onChange={(e) => setNewScope({ ...newScope, branch_name: e.target.value })}
            placeholder="e.g., feat/payment-revamp"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <BranchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Scope Type</InputLabel>
            <Select
              value={newScope.scope_type}
              label="Scope Type"
              onChange={(e) => setNewScope({ ...newScope, scope_type: e.target.value })}
            >
              <MenuItem value="feature">Feature</MenuItem>
              <MenuItem value="bugfix">Bug Fix</MenuItem>
              <MenuItem value="hotfix">Hotfix</MenuItem>
              <MenuItem value="epic">Epic</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={newScope.description}
            onChange={(e) => setNewScope({ ...newScope, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateScope}
            variant="contained"
            disabled={!newScope.name.trim() || creating}
            startIcon={creating ? <CircularProgress size={16} /> : null}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Issues Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Issues to{' '}
          <Typography component="span" sx={{ fontWeight: 700 }}>
            {assignTargetScope?.name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {unscopedIssues.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              All issues in this release are already assigned to a scope.
            </Alert>
          ) : (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder="Search unscoped issues..."
                value={assignSearch}
                onChange={(e) => setAssignSearch(e.target.value)}
                sx={{ mt: 1, mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
              {selectedIssueIds.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Chip
                    size="small"
                    label={`${selectedIssueIds.length} selected`}
                    color="primary"
                    onDelete={() => setSelectedIssueIds([])}
                  />
                </Box>
              )}
              <List dense sx={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: 1 }}>
                {filteredUnscopedIssues.map(issue => {
                  const checked = selectedIssueIds.includes(issue.id);
                  const label = issue.jira_bug_id || `QA-${issue.id}`;
                  return (
                    <ListItem key={issue.id} disablePadding>
                      <ListItemButton onClick={() => handleToggleIssue(issue.id)} dense>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple size="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748B', minWidth: 52 }}>
                                {label}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.82rem' }} noWrap>
                                {issue.title}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                              {issue.severity && <Chip label={issue.severity} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />}
                              {issue.status && <Chip label={issue.status} size="small" sx={{ fontSize: '0.65rem', height: 18 }} variant="outlined" />}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
                {filteredUnscopedIssues.length === 0 && assignSearch && (
                  <ListItem>
                    <ListItemText primary="No matching issues" sx={{ textAlign: 'center', color: 'text.secondary' }} />
                  </ListItem>
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssignIssues}
            variant="contained"
            disabled={selectedIssueIds.length === 0 || assigning}
            startIcon={assigning ? <CircularProgress size={16} /> : null}
          >
            {assigning ? 'Assigning...' : `Assign ${selectedIssueIds.length} issue${selectedIssueIds.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScopeSwimlaneView;
